import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { uwinAPI } from "../../../../services/uwin.api";
import { toast } from "react-toastify";
import "../uwin.css";
import CloseIcon from "@mui/icons-material/Close";

// Data-transformation utilities from the VaccinationModule layer.
// These are pure functions: no API calls, no component dependencies.
import {
  transformVillageData,
  transformHealthVillageData,
  transformWardData,
  transformHealthWardData,
  transformVaccineData,
  transformAshaData,
  transformVaccinationData,
} from "../VaccinationModule/vaccinationUtils";

// Child components
import MemberCard from "./MemberCard";
import LocationDropdowns from "./LocationDropdowns";
import { decryptOpenSSLAES } from "../uwin-decrypt";

// VaccinationContainer is the focused vaccination widget.
// It receives pre-fetched data as props and handles ONLY vaccine/dose selection.
// It does NOT make any API calls and does NOT own location dropdowns.
import VaccinationContainer from "../VaccinationModule/VaccinationContainer";

const getEligibilityErrorMessage = (response, fallback) => {
  if (response?._isError) {
    return response.error?.response?.data?.message?.error || fallback;
  }

  const message = response?.message ?? response?.data?.message;
  return message?.error || fallback;
};

// ============================================================================
// MEMBERVACCINATION - DATA-FETCHING COORDINATOR
// ============================================================================
//
// RESPONSIBILITIES (what this component owns):
//   - All 6 unified API calls (villages, materials, current, previous,
//     ASHA tagging, beneficiary profile)
//   - Location dropdown state + rendering (villages, healthVillages, wards, ASHA)
//   - Beneficiary type detection (PW vs CHILD) from the profile response
//   - LMC date / DOB / administeredVaccinations extraction
//   - The outer Formik form that owns location fields
//   - Passing all pre-fetched data down to VaccinationContainer
//
// NOT RESPONSIBILITIES (delegated to VaccinationContainer):
//   - Vaccine/dose selection logic
//   - Current/Previous tab state
//   - TD cascade validation
//   - Form submission (createVaccinationRecord)
//
// PROPS CONTRACT:
//   beneficiaryData    {Object}   - Beneficiary row from session list
//   beneficiaryType    {string}   - Optional: 'PW' | 'CHILD'.
//                                   Auto-detected from profile if not provided.
//   allowedTabs        {Array}    - Forwarded to VaccinationContainer
//   onContinue         {Function} - Called after successful standalone submission
//   toggleDrawer       {Function} - Closes the parent drawer
//   onFormComplete     {Function} - Stepper mode: called with form data for review
//   isStepperMode      {boolean}  - Controls VaccinationContainer submission mode
// ============================================================================

const MemberVaccination = ({
  beneficiaryData,
  beneficiaryType: beneficiaryTypeProp, // Optional - auto-detected if omitted
  allowedTabs = ['current', 'previous'],
  onContinue,
  toggleDrawer,
  onFormComplete,
  isStepperMode = false,
}) => {
  // ============================================================================
  // DATA LAYER STATE - owned exclusively by this component
  // ============================================================================

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Location data - rendered by LocationDropdowns, forwarded to VaccinationContainer
  // for payload assembly only (not for rendering inside VaccinationContainer)
  const [villages, setVillages] = useState([]);
  const [healthVillages, setHealthVillages] = useState([]);
  const [wards, setWards] = useState([]);
  const [healthWards, setHealthWards] = useState([]);
  const [ashaUsers, setAshaUsers] = useState([]);

  // Vaccination data passed down to VaccinationContainer
  const [vaccines, setVaccines] = useState([]);
  const [currentVaccinations, setCurrentVaccinations] = useState([]);
  const [previousVaccinations, setPreviousVaccinations] = useState([]);

  // Profile-derived data passed down to VaccinationContainer
  const [detectedBeneficiaryType, setDetectedBeneficiaryType] = useState(
    beneficiaryTypeProp || null
  );
  const [profileData, setProfileData] = useState(null); // { lmc_date } | { dob, childAgeInDays }
  const [administeredVaccinations, setAdministeredVaccinations] = useState([]);
  const [allUipMaterialsRaw, setAllUipMaterialsRaw] = useState([]);
  const [isCurrentFailed, setIsCurrentFailed] = useState(false);
  const [isPrevFailed, setIsPrevFailed] = useState(false);

  // ============================================================================
  // API DATA FETCHING
  // All 6 API calls fire in parallel via Promise.all.  This is intentionally
  // identical across MemberVaccination and any other data-owning parent so that
  // VaccinationContainer itself never needs to know where data came from.
  // ============================================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsCurrentFailed(false);
        setIsPrevFailed(false);
        console.log('MemberVaccination - fetching unified data');

        const [
          villageResponse,
          materialsResponse,
          currentVaccinationsResponse,
          previousVaccinationsResponse,
          ashaTaggingResponse,
          profileResponse,
        ] = await Promise.all([
          uwinAPI.getVillages({
            session_site_id: beneficiaryData?.session_site_id,
          }),
          uwinAPI.allUipMaterials(),
          uwinAPI.eligibleMaterialsCurrentVaccinations({
            beneficiary_id: beneficiaryData?.beneficiary_id,
            session_id: beneficiaryData?.session_id,
          }).catch(error => ({ _isError: true, error })),
          uwinAPI.eligibleMaterialsPrevVaccinations({
            beneficiary_id: beneficiaryData?.beneficiary_id,
          }).catch(error => ({ _isError: true, error })),
          uwinAPI.ashaVaccinationTagging({
            session_site_id: beneficiaryData?.session_site_id,
          }),
          uwinAPI.getBeneficiaryProfile({
            beneficiary_id: beneficiaryData?.beneficiary_id,
          }),
        ]);

        const currentFailed = currentVaccinationsResponse?._isError || currentVaccinationsResponse?.data?.success === false || currentVaccinationsResponse?.success === false;
        const prevFailed = previousVaccinationsResponse?._isError || previousVaccinationsResponse?.data?.success === false || previousVaccinationsResponse?.success === false;

        setIsCurrentFailed(currentFailed);
        setIsPrevFailed(prevFailed);

        if (currentFailed) {
          toast.error(
            getEligibilityErrorMessage(
              currentVaccinationsResponse,
              "Failed to load current vaccination eligibility. Please try again."
            )
          );
        }

        if (prevFailed) {
          toast.error(
            getEligibilityErrorMessage(
              previousVaccinationsResponse,
              "Failed to load previous vaccination eligibility. Please try again."
            )
          );
        }

        // Transform location data
        setVillages(transformVillageData(villageResponse));
        setHealthVillages(transformHealthVillageData(villageResponse));
        setWards(transformWardData(villageResponse));
        setHealthWards(transformHealthWardData(villageResponse));
        setAshaUsers(
          transformAshaData(ashaTaggingResponse?.data, decryptOpenSSLAES)
        );

        // Transform vaccination eligibility data.
        //
        // decryptOpenSSLAES is passed into both transform functions so that
        // material_id values (which the backend encrypts independently in each
        // API response) are decrypted to plain integers before any comparison.
        //
        // transformVaccineData:      decrypts material_id → stores as `id` (integer)
        // transformVaccinationData:  decrypts eligibility material_id → matches
        //                            against the already-decrypted ids in allVaccines
        //
        // This mirrors the pattern in Uwinsession.jsx where decryptOpenSSLAES is
        // called on both sides before comparing facility/session-site IDs.
        const transformedVaccines = transformVaccineData(materialsResponse, decryptOpenSSLAES);
        setVaccines(transformedVaccines);
        setAllUipMaterialsRaw(materialsResponse?.data || []);
        setCurrentVaccinations(
          currentFailed ? [] : transformVaccinationData(currentVaccinationsResponse, transformedVaccines, decryptOpenSSLAES, materialsResponse?.data)
        );
        setPreviousVaccinations(
          prevFailed ? [] : transformVaccinationData(previousVaccinationsResponse, transformedVaccines, decryptOpenSSLAES, materialsResponse?.data)
        );

        // Determine beneficiary type and extract profile data
        const fullProfile = profileResponse?.data;

        let resolvedType = beneficiaryTypeProp || null;

        if (!resolvedType) {
          if (fullProfile?.lmc_date) {
            resolvedType = 'PW';
          } else if (fullProfile?.dob) {
            resolvedType = 'CHILD';
          } else {
            console.error(
              'MemberVaccination: Unable to determine beneficiary type from profile:',
              fullProfile
            );
            throw new Error(
              'Unable to determine beneficiary type - missing LMC date and DOB in profile'
            );
          }
          console.log('MemberVaccination: auto-detected beneficiary type:', resolvedType);
        }

        setDetectedBeneficiaryType(resolvedType);
        setAdministeredVaccinations(fullProfile?.vaccination || []);

        // Build the profileData shape that VaccinationContainer and the scenario
        // hooks expect.  PW needs lmc_date, CHILD needs dob + childAgeInDays.
        if (resolvedType === 'PW') {
          setProfileData({ 
            lmc_date: fullProfile?.lmc_date,
            vaccination: fullProfile?.vaccination,
            edd_date: fullProfile?.edd_date || beneficiaryData?.edd_date
          });
        } else if (resolvedType === 'CHILD') {
          // Import calculateChildAgeInDays lazily to avoid circular dep warnings.
          // We dynamically call it only when needed, keeping the import at the
          // top of VaccinationContainer (where it is already imported) and here
          // we replicate the dayjs-based calculation inline to avoid crossing
          // module boundaries from the memberVaccination layer into VaccinationModule.
          const { calculateChildAgeInDays } = await import(
            '../VaccinationModule/scenarios/children-current'
          );
          const dob = fullProfile?.dob;
          const childAgeInDays = dob ? calculateChildAgeInDays(dob) : 0;
          setProfileData({ dob, childAgeInDays });
        }

        setIsDataLoaded(true);
      } catch (error) {
        console.error("MemberVaccination - fetchData error:", error);
        toast.error("Failed to load vaccination data. Please try again.");
        // Still mark as loaded so the UI shows an error state rather than
        // an infinite spinner.
        setIsDataLoaded(true);
      }
    };

    // Guard: only fetch when we have the minimum required identifiers
    if (beneficiaryData?.session_site_id && beneficiaryData?.beneficiary_id) {
      fetchData();
    }
  }, [beneficiaryData?.session_site_id, beneficiaryData?.beneficiary_id]);

  // ============================================================================
  // TAB AWARENESS
  // VaccinationContainer owns the tab index internally because it drives form
  // submission logic.  We only need to know whether the Previous tab is active
  // so we can hide LocationDropdowns.  The child notifies us via onTabChange.
  // ============================================================================
  const [isOnPreviousTab, setIsOnPreviousTab] = useState(false);

  const handleTabChange = (tabIndex) => {
    // Tab 0 = Current, Tab 1 = Previous — mirrors VaccinationContainer's convention
    setIsOnPreviousTab(tabIndex === 1);
  };

  // ============================================================================
  // LOCATION FORM
  // The outer Formik instance owns only the location fields.  Its values are
  // passed into VaccinationContainer via the locationFormValues prop so the
  // API service can read village/ward/ASHA ids when assembling the payload.
  // ============================================================================
  const locationValidationSchema = Yup.object({
    lgd_village: Yup.string(),
    health_village: Yup.string(),
    lgd_ward: Yup.string(),
    health_ward: Yup.string(),
    asha_user_id: Yup.string(),
    tag_to_session_site: Yup.boolean(),
  });

  const locationInitialValues = {
    lgd_village: "",
    health_village: "",
    lgd_ward: "",
    health_ward: "",
    asha_user_id: "",
    tag_to_session_site: false,
  };

  // ============================================================================
  // LOADING GUARD
  // While the beneficiary type is still being determined from the profile API
  // we render a full-page spinner rather than a partial UI.
  // ============================================================================
  if (!detectedBeneficiaryType && isDataLoaded) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
        }}
      >
        <Typography color="error" sx={{ fontSize: 14 }}>
          Unable to determine beneficiary type. Please close and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
        minWidth:"400px"
      }}
    >
      {/* Header */}
      <Box
        sx={{
          py: 2,
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 600,
            fontSize: '18px',
            color: '#111827',
          }}
        >
          Member Vaccination
        </Typography>

        {toggleDrawer && (
          <IconButton onClick={toggleDrawer(false)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Member summary */}
      <MemberCard beneficiaryData={beneficiaryData} />

      {/* Location form - owns lgd_village, health_village, lgd_ward, asha_user_id.
          The inner VaccinationContainer receives these values via locationFormValues
          so it can include them in the submission payload without duplicating state. */}
      <Formik
        initialValues={locationInitialValues}
        validationSchema={locationValidationSchema}
        // Location form itself never submits - submission is handled inside
        // VaccinationContainer's own Formik instance.
        onSubmit={() => {}}
      >
        {({ values: locationValues, errors, touched, setFieldValue }) => (
          // IMPORTANT: We deliberately render a <Box> instead of Formik's <Form>
          // here. <Form> would emit a real <form> HTML element, and because
          // VaccinationContainer contains its own <Formik><Form> inside it,
          // that would produce nested <form> elements — invalid HTML. The browser
          // resolves the ambiguity by submitting the OUTER form on any submit
          // event, which triggers a full page reload (the native default form
          // action) and wipes the console before the inner handleSubmit runs.
          // The outer Formik's sole purpose is to hold location field values in
          // Formik state so they can be read via locationValues and passed to
          // VaccinationContainer. It never needs to submit anything itself.
          <Box>
            {/* Location dropdowns are shown only on the Current tab.
                isOnPreviousTab is updated via the onTabChange callback passed
                to VaccinationContainer, which calls it whenever the tab changes. */}
            {!isOnPreviousTab && (
            <LocationDropdowns
              values={locationValues}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              villages={villages}
              healthVillages={healthVillages}
              wards={wards}
              healthWards={healthWards}
              ashaUsers={ashaUsers}
            />)}

            {/* VaccinationContainer is the focused vaccination widget.
                It receives all pre-fetched data as props and handles the
                vaccine/dose selection form and submission internally.
                It does NOT call any APIs or render location dropdowns. */}
            {detectedBeneficiaryType && (
              <VaccinationContainer
                isCurrentFailed={isCurrentFailed}
                isPrevFailed={isPrevFailed}
                // Pre-fetched data - no API calls inside VaccinationContainer
                vaccines={vaccines}
                currentVaccinations={currentVaccinations}
                previousVaccinations={previousVaccinations}
                profileData={profileData}
                administeredVaccinations={administeredVaccinations}
                allUipMaterialsRaw={allUipMaterialsRaw}
                villages={villages}
                healthVillages={healthVillages}
                wards={wards}
                healthWards={healthWards}
                // Beneficiary context
                beneficiaryData={beneficiaryData}
                beneficiaryType={detectedBeneficiaryType}
                // Tab configuration forwarded from parent prop
                allowedTabs={allowedTabs}
                // Loading gate - VaccinationContainer won't show data until true
                isDataLoaded={isDataLoaded}
                // Location values from the outer Formik form are injected here
                // so VaccinationContainer can include them in the API payload
                // without owning location state itself.
                locationFormValues={locationValues}
                // Callbacks
                onContinue={onContinue}
                // toggleDrawer is not passed here because the header X button is
                // already rendered by MemberVaccination above.  Passing it again
                // would render a duplicate close button inside VaccinationContainer.
                onFormComplete={onFormComplete}
                isStepperMode={isStepperMode}
                // Tab change notification - VaccinationContainer calls this whenever
                // the user switches tabs so this parent can show/hide LocationDropdowns.
                onTabChange={handleTabChange}
              />
            )}

            {/* Loading state while data is being fetched */}
            {!isDataLoaded && (
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} />
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: 14,
                    color: 'text.secondary',
                    fontFamily:
                      'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                >
                  Loading vaccination data...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default MemberVaccination;
